import { useCallback, useState } from "react";
import { isProductInAReception } from "$services/Reception.service";
import { isProductInAnOrder } from "$services/Order.service";
import { EDBService, ProductService } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";
import { isReferencingCategoryName } from "$hooks/useCategoriesQuery";

const useIsProductDeletable = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isDeletable = useCallback(
    async (
      productReference,
      companyId?: string,
      excludedReceptionId?: number
    ) => {
      const endFalse = () => {
        setIsLoading(false);
        return false;
      };

      setIsLoading(true);
      if (!productReference) {
        // eslint-disable-next-line no-console
        console.log(
          "[isDeletable] productReference not defined ! --> still returns true (''it is deletable'') but prevent a call to '/api/product/withoutCache' without enough filters which could cause JavaScript heap out of memory in production"
        );
        setIsLoading(false);
        return true;
      }

      const product = await queryClient.fetchQuery(
        [
          CacheInvalidationAction.GET_PRODUCT_BY_REFERENCE,
          productReference,
          companyId,
        ],
        () =>
          ProductService.getProductByReference({
            reference: productReference,
            company: companyId,
          }),
        {
          cacheTime: 1000 * 60,
        }
      );

      if (!product || product.length <= 0) {
        // eslint-disable-next-line no-console
        console.log("[isDeletable] Product not found ! --> Can't be deleted");
        return endFalse();
      }

      // Is product in an order ?
      const isInAnOrder = await queryClient.fetchQuery(
        [
          CacheInvalidationAction.IS_PRODUCT_IN_ORDER,
          product[0].reference,
          companyId,
        ],
        () => isProductInAnOrder(product[0].reference, companyId),
        {
          cacheTime: 1000 * 60,
        }
      );
      if (isInAnOrder) {
        // eslint-disable-next-line no-console
        console.log(
          "[isDeletable] Product is in an order --> Can't be deleted"
        );
        return endFalse();
      }

      // Is product in a reception ?
      const isInAReception = await queryClient.fetchQuery(
        [
          CacheInvalidationAction.IS_PRODUCT_IN_A_RECEPTION,
          product[0].reference,
          companyId,
          excludedReceptionId,
        ],
        () =>
          isProductInAReception(
            product[0].reference,
            excludedReceptionId,
            companyId
          ),
        {
          cacheTime: 1000 * 60,
        }
      );
      if (isInAReception) {
        if (
          !product[0].categories.some((category) =>
            isReferencingCategoryName(category.title)
          )
        ) {
          // eslint-disable-next-line no-console
          console.log(
            "[isDeletable] Product is in a reception --> Can't be deleted"
          );
          return endFalse();
        }
      }

      const isInAnEdb = await queryClient.fetchQuery(
        [
          CacheInvalidationAction.CHECK_PRODUCT_EXIST_IN_EDB,
          { products: product?.[0]._id },
        ],
        () => EDBService.checkProductExistInEdb(product?.[0]._id),
        { cacheTime: 5 * 60 * 1000 }
      );

      if (isInAnEdb) {
        // eslint-disable-next-line no-console
        console.log("[isDeletable] Product is in an EDB --> Can't be deleted");
        return endFalse();
      }

      setIsLoading(false);
      return true;
    },
    []
  );

  return {
    isDeletable,
    isLoading,
  };
};

export default useIsProductDeletable;
